<template>
  <iColumn>
    <iHeading v-if="showHeading">
      Advertising Settings
    </iHeading>
    <iText variant="subtle">
      Create ads to acquire new fans, and earn money showing ads to your community.
    </iText>
    <iColumn gap="extraLarge">
      <iColumn>
        <iTitle>
          Earn Money Displaying Ads
        </iTitle>
        <iText variant="subtle">
          Some ads will compensate you simply for showing them to your fans, others will reward
          you if and when your fans subscribe to the advertising publication.
        </iText>
        <iCheckbox
          :model-value="isAdsEnabled"
          label="Show ads on my publication"
          :is-loading="isLoadingAds"
          @update:model-value="toggleAds"
        />
        <iSpace :height="1" />
        <iColumn gap="small">
          <iRow vertical-align="middle" wrap="nowrap" gap="extraSmall">
            <iText font-weight="dark">
              Choose which ads will appear
            </iText>
          </iRow>
          <iColumn gap="large">
            <iRow gap="none">
              <iRadioButton
                v-model="adsApprovalMode"
                name="adsApprovalMode"
                label="Automatic Selection (recommended)"
                value="automatic"
              />
              <iRow wrap="nowrap">
                <iColumn width="hug">
                  <iSpace :width="20" />
                </iColumn>
                <iColumn>
                  <iText variant="subtle" size="small">
                    Algorithmically choose lucrative & targeted ads for my fans
                  </iText>
                </iColumn>
              </iRow>
            </iRow>
            <iRow gap="none">
              <iRadioButton
                v-model="adsApprovalMode"
                name="adsApprovalMode"
                label="Manual Selection (coming soon)"
                value="manual"
                disabled
              />
              <iRow wrap="nowrap">
                <iColumn width="hug">
                  <iSpace :width="20" />
                </iColumn>
                <iColumn>
                  <iText variant="subtle" size="small">
                    Browse and select which ads you want to display on your publication
                  </iText>
                </iColumn>
              </iRow>
            </iRow>
          </iColumn>
        </iColumn>
      </iColumn>
      <iColumn>
        <iColumn gap="small">
          <iTitle>
            Expand your Audience by Advertising
          </iTitle>
          <iText variant="subtle">
            There are 2 ways to pay for advertising: 1) you can buy exposure, or
            2) share revenue when someone refers you a new subscriber.
            You can also do a combination of both.
          </iText>
        </iColumn>
        <iSpace :height="2" />
        <iText font-weight="dark">
          Refer & Earn
        </iText>
        <iCheckbox
          :model-value="isPayForReferralsEnabled"
          label="Pay other creators if they refer me subscribers"
          :is-loading="isLoadingPayForReferrals"
          @update:model-value="togglePayForReferrals"
        />
        <iColumn gap="small">
          <iText variant="subtle">
            Any ad that turns into a new subscriber will reward the publisher with a commission.
          </iText>
          <iText>
            We automatically redirect a portion of subscription revenue to referring publishers as a reward
          </iText>
          <iText>
            $10 for each new monthly subscriber (first month)
          </iText>
          <iText>
            $12 for each new annual subscriber (10%)
          </iText>
          <iParagraph variant="subtle">
            Creators can use your ads or promote you in their own creative way using unique tracking links.
          </iParagraph>
        </iColumn>
      </iColumn>
      <iColumn>
        <iRow vertical-align="middle">
          <iText font-weight="dark">
            Ad units
          </iText>
          <iButton
            variant="secondary"
            size="small"
            @click="openCreateAdModal"
          >
            Create Ad
          </iButton>
        </iRow>
        <SmartFeed
          :source="useMyAdsFeedStore"
          default-entity-layout="small"
          default-feed-layout="carousel"
        >
          <template #empty-state>
            <iRow vertical-align="middle">
              <iText variant="subtle">
                No ads yet
              </iText>
            </iRow>
          </template>
        </SmartFeed>
      </iColumn>
    </iColumn>
  </iColumn>
</template>

<script>
import { mapActions, mapState } from "pinia";
import { usePublicationAdsStore } from "%/stores/publication-ads-store";
import SmartFeed from "SmartFeed/components/SmartFeedV2.vue";
import { useMyAdsFeedStore } from "@/smart-feed/stores/feed/my-ads-feed-store";
import { useCreatorPublicationStore } from "%/stores/creator-publication-store";

export default {
  name: "PublicationAdvertisingSettings",
  components: { SmartFeed },
  props: {
    publication: {
      type: Object,
      required: true,
    },
    showHeading: {
      type: Boolean,
      required: false,
      default: true,
    },
  },
  data() {
    return {
      adsApprovalMode: "automatic",
    };
  },
  computed: {
    ...mapState(usePublicationAdsStore, [
      "isEnableAdsLoading",
      "isDisableAdsLoading",
    ]),
    ...mapState(useCreatorPublicationStore, [
      "isLoadingDisablePayForReferrals",
      "isLoadingEnablePayForReferrals",
    ]),
    isAdsEnabled() {
      return this.publication.enable_ads === 1;
    },
    isPayForReferralsEnabled() {
      return this.publication.pay_for_referrals === 1;
    },
    isLoadingAds() {
      return this.isEnableAdsLoading || this.isDisableAdsLoading;
    },
    isLoadingPayForReferrals() {
      return this.isLoadingEnablePayForReferrals || this.isLoadingDisablePayForReferrals;
    },
  },
  methods: {
    useMyAdsFeedStore,
    ...mapActions(usePublicationAdsStore, [
      "enableAds",
      "disableAds",
      "openCreateAdModal",
    ]),
    ...mapActions(useCreatorPublicationStore, ["enablePayForReferrals", "disablePayForReferrals"]),
    toggleAds(value) {
      if (value) {
        this.enableAds(this.publication.publication_id);
      } else {
        this.disableAds(this.publication.publication_id);
      }
    },
    togglePayForReferrals(value) {
      if (value) {
        this.enablePayForReferrals(this.publication.publication_id);
      } else {
        this.disablePayForReferrals(this.publication.publication_id);
      }
    },
  },
};
</script>

<style scoped lang="scss"></style>
